import {
    Table,
    TableBody,
    TableCellText,
    TablePagination,
    TableBottomBar,
} from "@ceridianhcm/components";
import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { StandAloneActions } from "../../../models/StandAloneData";
import StandAloneContext from "../context/StandAloneContext";
import { TableProps } from "./table.props.model";
import "./table.scss";
import NoDataFallback from "./SubComponents/NoDataFallback/NoDataFallback";

const defaultCellTemplate = (cellContext: any) => (
    <TableCellText>{cellContext.getValue()}</TableCellText>
);

export const TableV: React.FC<TableProps> = ({
    testId,
    data = [],
    dataKey,
    dataLength,
    columns = [],
    description,
    noDataText,
    addItemText,
    tableHeight = "400px",
    isLoading = false,
    pageIndex: controlledPageIndex,
    toolBarItems,
    rowsPerPage: controlledRowsPerPage = 50,
    rowsPerPageOptions = [10, 25, 50, 100],
    serverSide,
    onPageChange,
    onRowsPerPageChange,
    getNextPageDetailsFromAPI,
}) => {

    const { dispatch } = useContext(StandAloneContext);

    // Manage controlled/uncontrolled pageIndex and rowsPerPage (internal mirrors)
    const [pageIndex, setPageIndex] = useState(controlledPageIndex ?? 0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(controlledRowsPerPage);

    // Sync with external pageIndex prop changes
    // Sync with external rowsPerPage prop changes (controlled usage)
    useEffect(() => {
        if (controlledRowsPerPage !== rowsPerPage) {
            setRowsPerPage(controlledRowsPerPage);
        }
    }, [controlledRowsPerPage, rowsPerPage]);

    // Determine if we are in server-side mode (data already represents current page)
    const inferredServerSide = useMemo(() => {
        if (serverSide != null) return serverSide;
        if (typeof dataLength === "number" && Array.isArray(data)) {
            return dataLength > data.length; // heuristic: total > current slice size => server paging
        }
        return false;
    }, [serverSide, dataLength, data]);


    const visibleData = useMemo(() => {
        if (!data || data.length === 0) return [];
        if (inferredServerSide) return data; // server returns already paged slice
        const start = pageIndex * rowsPerPage;
        return (data as any[]).slice(start, start + rowsPerPage);
    }, [data, pageIndex, rowsPerPage, inferredServerSide]);


    const tableBodyRef = useRef(null);

    const invokeServerOrCallback = () => {
        if (getNextPageDetailsFromAPI) {
            getNextPageDetailsFromAPI();
        }
    };

    const onPageChangeHandler = (newPageIndex: number) => {
        if (newPageIndex === pageIndex) return;
        setPageIndex(newPageIndex);
        dispatch({
            type: StandAloneActions.PAGING,
            payload: { pageIdx: newPageIndex },
        });
        onPageChange?.(newPageIndex);
        if (inferredServerSide) {
            invokeServerOrCallback();
        }
    (tableBodyRef.current as unknown as { scrollToRow?: (row:number)=>void })?.scrollToRow?.(0);
    };

    const onRowsPerPageChangeHandler = (newRowsPerPage: number) => {
        if (newRowsPerPage === rowsPerPage) return;
        setRowsPerPage(newRowsPerPage);
        const resetPage = 0;
        setPageIndex(resetPage);
        dispatch({
            type: StandAloneActions.PAGING,
            payload: { pageIdx: resetPage, pageSize: newRowsPerPage },
        });
        onRowsPerPageChange?.(newRowsPerPage);
     
        if (inferredServerSide) {
            invokeServerOrCallback();
        }
    (tableBodyRef.current as unknown as { scrollToRow?: (row:number)=>void })?.scrollToRow?.(0);
    };


    useEffect(() => {
        if (typeof dataLength !== "number" || dataLength < 0) return;
        const totalPages = Math.max(1, Math.ceil(dataLength / rowsPerPage));
        if (pageIndex >= totalPages) {
            const newPage = totalPages - 1;
            if (newPage !== pageIndex) {
                setPageIndex(newPage);
                dispatch({
                    type: StandAloneActions.PAGING,
                    payload: { pageIdx: newPage },
                });
                onPageChange?.(newPage);
                if (inferredServerSide) {
                    invokeServerOrCallback();
                }
            }
        }
    }, [dataLength, rowsPerPage, pageIndex, inferredServerSide, dispatch, onPageChange]);

    const formattedDescription =
        description && description.length > 0
            ? description.charAt(0).toUpperCase() + description.slice(1)
            : "";

    // If columns prop has no cellTemplate, add default TableCellText wrapper (optional)
    const enhancedColumns = useMemo(() => (
        columns.map(col => !col.cellTemplate ? { ...col, cellTemplate: defaultCellTemplate } : col)
    ), [columns]);

  
    const rangeText = useMemo(() => {
        if (!dataLength || dataLength === 0) return "";
        const count = visibleData?.length ?? 0;
        if (count === 0) return ""; // avoid invalid range like X to X-1 when slice empty
        const start = pageIndex * rowsPerPage + 1;
        const end = start + count - 1;
        return `Showing ${start} to ${Math.min(end, dataLength)} of ${dataLength} entries`;
    }, [pageIndex, rowsPerPage, visibleData, dataLength]);

    return (
        <div
            style={{ width: "100%", marginTop: "20px", maxHeight: tableHeight }}
            id="everest-table"
        >
            <h3 className="evrHeading3">{formattedDescription}</h3>

            <Table id={`${testId}-table`} loading={isLoading}>
                {/* Optional Toolbar - uncomment if you want to add toolbar */}
                {/* <TableTopBarV2 items={toolBarItems} /> */}

                <TableBody
                    id={`${testId}-body`}
                    testId={`${testId}-body`}
                    data={visibleData}
                    columnDefs={enhancedColumns}
                    ariaLabel={`${formattedDescription} Table`}
                    rowDensity="compact"
                    ref={tableBodyRef}
                    alternatingRowColors
                    textMap={{
                        loading: "Fetching Data...",
                    }}
                />

                <TableBottomBar id="table-bottom-bar">
                    <div className="evrTableRangeText" aria-live="polite" style={{ padding: "0 12px", fontSize: "12px" }}>{rangeText}</div>
                    <TablePagination
                        id={`${testId}-pagination-controls`}
                        testId={`${testId}-pagination-controls`}
                        currentPageIndex={pageIndex}
                        onPageIndexChange={onPageChangeHandler}
                        onRowsPerPageChange={onRowsPerPageChangeHandler}
                        currentRowsPerPage={rowsPerPage}
                        totalRowsCount={dataLength ?? 0}
                        rowsPerPageOptions={rowsPerPageOptions}
                        textMap={{
                            previousPageAriaLabel: "Previous Page",
                            nextPageAriaLabel: "Next Page",
                            rowsPerPage: "Rows per page",
                            rowsPerPageDropdownAriaLabel: "Rows per page",
                            page: "Page",
                            pageDropdownAriaLabel: "Page",
                            navigationAriaLabel: "Pagination navigation",
                        }}
                    />
                </TableBottomBar>
            </Table>

            {!isLoading && (
                inferredServerSide ? ((dataLength ?? 0) === 0) : (!data || data.length === 0)
            ) && (
                <NoDataFallback
                    maxHeight={tableHeight}
                    testId={testId}
                    noDataText={noDataText}
                    message={addItemText}
                />
            )}
        </div>
    );
};
